import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { resolveMailLanguage } from "../../emailLanguage";
import { withEntryQuery } from "../../entryParams";
import { CANCELLATION_EMAIL, PRIVACY_URL } from "../../externalLinks";
import { formatDate } from "../../format";
import { ApiFailure } from "../../restClient";
import { CANCELLATION_PATH } from "../../routes";
import {
  CancellationFormValues,
  CancellationPreview,
  CancellationReceipt,
  PROBLEM_FIELDS,
  confirmCancellation,
  previewCancellation,
} from "./cancellationApi";
import "../public-page.css";

/** Which of the three steps of this page is on screen. */
type Stage = "form" | "review" | "done";

/** Field names that can carry a validation message. */
type FieldError = keyof CancellationFormValues;

const EMPTY_FORM: CancellationFormValues = {
  name: "",
  email: "",
  contractReference: "",
  kind: "ordinary",
  extraordinaryReason: "",
  timing: "nextPossible",
  desiredEffectiveOn: "",
  note: "",
};

// Deliberately loose: this only catches a typo before the request goes out.
// Whether the address exists is decided by the confirmation mail, never here.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirror the column lengths on CancellationRequest so an over-long entry is
// stopped in the field rather than coming back as a 400.
const MAX_NAME = 200;
const MAX_EMAIL = 320;
const MAX_CONTRACT_REFERENCE = 200;

// ⚠️ These two share one column: composeNote() puts both free-text fields into
// SubmittedNote's 2000 characters, so they are budgeted against 2000 together.
// Giving each the whole column would let a valid form come back as a NoteTooLong
// 400 that PROBLEM_FIELDS can only pin on the note.
const MAX_NOTE = 1200;
const MAX_EXTRAORDINARY_REASON = 600;

// What the column holds, as the last line of defence: collectErrors() measures
// the composed note against it, so widening one of the two fields fails in the
// form and not with an unattributable 400.
const MAX_SUBMITTED_NOTE = 2000;

/**
 * Step two of the login-free cancellation. The five inputs are the ones
 * § 312k Abs. 2 Satz 3 BGB prescribes.
 *
 * ⚠️ This page shows no contract data on purpose: an answer like "we found your
 * subscription" would make the form an account oracle open to anyone. The preview
 * only echoes the declaration back, and everything about the contract goes into
 * the confirmation mail.
 *
 * ⚠️ The cancellation is effective on submit - the mail is a receipt
 * (§ 312k Abs. 3), not a step of the process.
 */
function CancellationConfirmPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const locale = i18n.language || "de";
  const language = resolveMailLanguage(locale);

  const [stage, setStage] = useState<Stage>("form");
  const [values, setValues] = useState<CancellationFormValues>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<CancellationPreview | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [receipt, setReceipt] = useState<CancellationReceipt | null>(null);

  const setValue = <K extends keyof CancellationFormValues>(
    key: K,
    value: CancellationFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const showDate = (isoDate: string | null | undefined): string =>
    isoDate ? formatDate(isoDate, locale) : t("cancellation.confirm.unknown");

  /** The address a cancellation can always be sent to instead (AGB 17). */
  const contactEmail =
    (preview && preview.contactEmail) ||
    (failure && failure.body && failure.body.contactEmail) ||
    CANCELLATION_EMAIL;

  /**
   * The note as it will be recorded. Only the extraordinary reason is folded in -
   * the kind and the effective date have their own columns, which the backend
   * reads. The reason has none yet, so it stays here in the customer's own words.
   */
  const composeNote = (): string => {
    const lines: string[] = [];
    const own = values.note.trim();

    if (own) {
      lines.push(own);
    }

    if (values.kind === "extraordinary" && values.extraordinaryReason.trim()) {
      lines.push(
        t("cancellation.confirm.noteLines.reason", {
          value: values.extraordinaryReason.trim(),
        }),
      );
    }

    return lines.join("\n");
  };

  const collectErrors = (): FieldError[] => {
    const errors: FieldError[] = [];

    if (values.name.trim().length === 0) {
      errors.push("name");
    }
    if (!EMAIL_PATTERN.test(values.email.trim())) {
      errors.push("email");
    }
    // The contract designation is not required: demanding a wording only we
    // would recognise would be a hurdle in front of the button.
    if (
      values.kind === "extraordinary" &&
      values.extraordinaryReason.trim().length === 0
    ) {
      errors.push("extraordinaryReason");
    }
    if (values.timing === "specificDate" && !values.desiredEffectiveOn) {
      errors.push("desiredEffectiveOn");
    }

    // The maxLengths are budgeted so this cannot trigger; it stays as the guard
    // that names the right field if it ever does, since the backend can only
    // answer NoteTooLong and PROBLEM_FIELDS would always mark the note.
    if (composeNote().length > MAX_SUBMITTED_NOTE) {
      errors.push(
        values.extraordinaryReason.trim().length > values.note.trim().length
          ? "extraordinaryReason"
          : "note",
      );
    }

    return errors;
  };

  const hasError = (field: FieldError) => fieldErrors.indexOf(field) >= 0;

  /** Turns a rejected submission into a field marker where it names one. */
  const applyFailure = (result: ApiFailure) => {
    setFailure(result);

    const problem = result.body && result.body.problem;
    const field = problem ? PROBLEM_FIELDS[problem] : undefined;

    setFieldErrors(field ? [field] : []);
    if (field) {
      setStage("form");
    }
  };

  /** Form step: hand the declaration to the backend and render it back. */
  const goToReview = async (event: FormEvent) => {
    event.preventDefault();

    const errors = collectErrors();
    setFieldErrors(errors);
    if (errors.length > 0) {
      return;
    }

    setBusy(true);
    setFailure(null);

    const result = await previewCancellation(values, composeNote(), language);

    setBusy(false);

    if (!result.ok) {
      applyFailure(result);
      return;
    }

    setPreview(result.data);
    setStage("review");
  };

  /** Review step: submit the declaration. Effective the moment this returns. */
  const submit = async () => {
    setBusy(true);
    setFailure(null);

    const result = await confirmCancellation(values, composeNote(), language);

    setBusy(false);

    if (!result.ok) {
      applyFailure(result);
      return;
    }

    setReceipt(result.data);
    setStage("done");
  };

  /**
   * The sentence about when the contract ends. The backend names a rule rather
   * than a date; an unknown rule falls back to the general wording.
   */
  const contractEndSentence = (rule: string | undefined): string =>
    t(`cancellation.confirm.contractEndRule.${rule || "unknown"}`, {
      defaultValue: t("cancellation.confirm.contractEndRule.unknown"),
    });

  /** The failure banner. Every reason names a way to cancel that still works. */
  const failureNotice = () => {
    if (!failure) {
      return null;
    }

    const problem = failure.body && failure.body.problem;

    return (
      <div className="pub-notice pub-notice-error" role="alert">
        <p>
          {problem
            ? t(`cancellation.confirm.problem.${problem}`, {
                defaultValue: t(`cancellation.confirm.failure.${failure.reason}`),
              })
            : t(`cancellation.confirm.failure.${failure.reason}`)}
        </p>
        <p>
          {t("cancellation.confirm.failure.fallback")}{" "}
          <a href={`mailto:${contactEmail}`} className="pub-link">
            {contactEmail}
          </a>
        </p>
      </div>
    );
  };

  if (stage === "done" && receipt) {
    return (
      <div className="pub-page">
        <h1 className="pub-title">{t("cancellation.done.title")}</h1>

        <div className="pub-notice pub-notice-success" role="status">
          <p>
            {t("cancellation.done.effective", {
              date: showDate(receipt.receivedAt),
            })}
          </p>
          <p>{contractEndSentence(preview ? preview.contractEndRule : undefined)}</p>
        </div>

        <section className="pub-panel" aria-label={t("cancellation.done.title")}>
          {/*
            ⚠️ The same sentence for everyone. The § 312k Abs. 3 confirmation goes
            out only when the declaration could be matched to an account, but a
            wording that differed would turn this form into a customer lookup, so
            it promises only what is true in both cases.
          */}
          <p className="pub-text">
            {t("cancellation.done.mail", { email: values.email.trim() })}
          </p>
          <p className="pub-text">
            {t("cancellation.done.reference", { reference: receipt.requestId })}
          </p>
          <p className="pub-text">{t("cancellation.done.account")}</p>
          <p className="pub-text">
            {t("cancellation.done.questions")}{" "}
            <a href={`mailto:${contactEmail}`} className="pub-link">
              {contactEmail}
            </a>
          </p>
        </section>

        <button
          type="button"
          className="pub-secondary-btn"
          onClick={() => navigate(withEntryQuery(CANCELLATION_PATH))}
        >
          {t("cancellation.done.back")}
        </button>
      </div>
    );
  }

  if (stage === "review" && preview) {
    return (
      <div className="pub-page">
        <button
          type="button"
          className="pub-back-link"
          onClick={() => setStage("form")}
        >
          {t("cancellation.confirm.backToForm")}
        </button>

        <h1 className="pub-title">{t("cancellation.confirm.reviewTitle")}</h1>
        <p className="pub-lead">{t("cancellation.confirm.reviewLead")}</p>

        {failureNotice()}

        <section
          className="pub-panel"
          aria-label={t("cancellation.confirm.recordedTitle")}
        >
          <h2 className="pub-panel-title">
            {t("cancellation.confirm.recordedTitle")}
          </h2>
          <dl className="pub-facts">
            <div className="pub-fact">
              <dt>{t("cancellation.confirm.fields.name")}</dt>
              <dd>{preview.name}</dd>
            </div>
            <div className="pub-fact">
              <dt>{t("cancellation.confirm.fields.email")}</dt>
              <dd>{preview.email}</dd>
            </div>
            <div className="pub-fact">
              <dt>{t("cancellation.confirm.fields.contractReference")}</dt>
              <dd>
                {preview.contractReference ||
                  t("cancellation.confirm.notStated")}
              </dd>
            </div>
            {/*
              § 312k Abs. 2 Satz 4 BGB: show the customer the declaration he is
              about to make. Kind and timing come from the form, not the preview -
              the backend echoes only what it stores as text.
            */}
            <div className="pub-fact">
              <dt>{t("cancellation.confirm.fields.kind")}</dt>
              <dd>{t(`cancellation.confirm.kind.${values.kind}`)}</dd>
            </div>
            <div className="pub-fact">
              <dt>{t("cancellation.confirm.fields.timing")}</dt>
              <dd>
                {values.timing === "specificDate"
                  ? t("cancellation.confirm.timing.specificDateValue", {
                      date: showDate(values.desiredEffectiveOn),
                    })
                  : t("cancellation.confirm.timing.nextPossible")}
              </dd>
            </div>
            <div className="pub-fact">
              <dt>{t("cancellation.confirm.fields.declaration")}</dt>
              <dd className="pub-fact-multiline">
                {preview.note || t("cancellation.confirm.notStated")}
              </dd>
            </div>
          </dl>
        </section>

        <section
          className="pub-panel"
          aria-label={t("cancellation.confirm.submitAria")}
        >
          {/*
            Both sentences are mandatory content: the button IS the cancellation
            (§ 312k Abs. 2), and the contract's end follows AGB 16/17, which is a
            different date and must not be confused with this one.
          */}
          <p className="pub-text">
            {preview.effectiveOnSubmit
              ? t("cancellation.confirm.effectiveOnSubmit")
              : t("cancellation.confirm.submitNote")}
          </p>
          <p className="pub-text">
            {contractEndSentence(preview.contractEndRule)}
          </p>

          <button
            type="button"
            className="pub-primary-btn pub-primary-btn-block"
            disabled={busy}
            onClick={submit}
          >
            {busy
              ? t("cancellation.confirm.submitting")
              : t("cancellation.confirm.submitButton")}
          </button>
          <p className="pub-submit-hint">
            {t("cancellation.confirm.submitHint")}
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="pub-page">
      <button
        type="button"
        className="pub-back-link"
        onClick={() => navigate(withEntryQuery(CANCELLATION_PATH))}
      >
        {t("cancellation.confirm.back")}
      </button>

      <h1 className="pub-title">{t("cancellation.confirm.title")}</h1>
      <p className="pub-lead">{t("cancellation.confirm.lead")}</p>

      {failureNotice()}

      <form onSubmit={goToReview} noValidate>
        <section
          className="pub-panel"
          aria-label={t("cancellation.confirm.detailsTitle")}
        >
          <h2 className="pub-panel-title">
            {t("cancellation.confirm.detailsTitle")}
          </h2>

          <div className="pub-field">
            <label className="pub-label" htmlFor="cancellation-name">
              {t("cancellation.confirm.fields.name")}
            </label>
            <input
              id="cancellation-name"
              className={`pub-input${hasError("name") ? " pub-input-invalid" : ""}`}
              type="text"
              autoComplete="name"
              maxLength={MAX_NAME}
              value={values.name}
              onChange={(event) => setValue("name", event.target.value)}
            />
            {hasError("name") ? (
              <span className="pub-field-error">
                {t("cancellation.confirm.errors.name")}
              </span>
            ) : null}
          </div>

          <div className="pub-field">
            <label className="pub-label" htmlFor="cancellation-email">
              {t("cancellation.confirm.fields.email")}
            </label>
            <input
              id="cancellation-email"
              className={`pub-input${hasError("email") ? " pub-input-invalid" : ""}`}
              type="email"
              autoComplete="email"
              maxLength={MAX_EMAIL}
              value={values.email}
              onChange={(event) => setValue("email", event.target.value)}
            />
            <span className="pub-hint">
              {t("cancellation.confirm.hints.email")}
            </span>
            {hasError("email") ? (
              <span className="pub-field-error">
                {t("cancellation.confirm.errors.email")}
              </span>
            ) : null}
          </div>

          <div className="pub-field">
            <label className="pub-label" htmlFor="cancellation-contract">
              {t("cancellation.confirm.fields.contractReference")}{" "}
              <span className="pub-optional">
                {t("cancellation.confirm.optional")}
              </span>
            </label>
            <input
              id="cancellation-contract"
              className={`pub-input${
                hasError("contractReference") ? " pub-input-invalid" : ""
              }`}
              type="text"
              maxLength={MAX_CONTRACT_REFERENCE}
              value={values.contractReference}
              onChange={(event) =>
                setValue("contractReference", event.target.value)
              }
            />
            <span className="pub-hint">
              {t("cancellation.confirm.hints.contractReference")}
            </span>
            {hasError("contractReference") ? (
              <span className="pub-field-error">
                {t("cancellation.confirm.errors.contractReference")}
              </span>
            ) : null}
          </div>
        </section>

        {/*
          § 312k Abs. 2 Satz 3 Nr. 1 and Nr. 4 BGB require the confirmation page
          to let the customer state kind and effective date.
        */}
        <section
          className="pub-panel"
          aria-label={t("cancellation.confirm.declarationTitle")}
        >
          <h2 className="pub-panel-title">
            {t("cancellation.confirm.declarationTitle")}
          </h2>

          <div className="pub-field">
            <span className="pub-label">
              {t("cancellation.confirm.fields.kind")}
            </span>
            <div className="pub-choices">
              <label className="pub-choice">
                <input
                  type="radio"
                  name="cancellation-kind"
                  checked={values.kind === "ordinary"}
                  onChange={() => setValue("kind", "ordinary")}
                />
                <span>{t("cancellation.confirm.kind.ordinary")}</span>
              </label>
              <label className="pub-choice">
                <input
                  type="radio"
                  name="cancellation-kind"
                  checked={values.kind === "extraordinary"}
                  onChange={() => setValue("kind", "extraordinary")}
                />
                <span>{t("cancellation.confirm.kind.extraordinary")}</span>
              </label>
            </div>
          </div>

          {values.kind === "extraordinary" ? (
            <div className="pub-field">
              <label className="pub-label" htmlFor="cancellation-reason">
                {t("cancellation.confirm.fields.extraordinaryReason")}
              </label>
              <textarea
                id="cancellation-reason"
                className={`pub-textarea${
                  hasError("extraordinaryReason") ? " pub-input-invalid" : ""
                }`}
                maxLength={MAX_EXTRAORDINARY_REASON}
                value={values.extraordinaryReason}
                onChange={(event) =>
                  setValue("extraordinaryReason", event.target.value)
                }
              />
              {hasError("extraordinaryReason") ? (
                <span className="pub-field-error">
                  {/*
                    Two distinct problems - missing, and long enough to push the
                    composed note past the column - so two messages.
                  */}
                  {values.extraordinaryReason.trim().length === 0
                    ? t("cancellation.confirm.errors.extraordinaryReason")
                    : t("cancellation.confirm.errors.extraordinaryReasonTooLong")}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="pub-field">
            <span className="pub-label">
              {t("cancellation.confirm.fields.timing")}
            </span>
            <div className="pub-choices">
              <label className="pub-choice">
                <input
                  type="radio"
                  name="cancellation-timing"
                  checked={values.timing === "nextPossible"}
                  onChange={() => setValue("timing", "nextPossible")}
                />
                <span>{t("cancellation.confirm.timing.nextPossible")}</span>
              </label>
              <label className="pub-choice">
                <input
                  type="radio"
                  name="cancellation-timing"
                  checked={values.timing === "specificDate"}
                  onChange={() => setValue("timing", "specificDate")}
                />
                <span>{t("cancellation.confirm.timing.specificDate")}</span>
              </label>
            </div>
          </div>

          {values.timing === "specificDate" ? (
            <div className="pub-field">
              <label className="pub-label" htmlFor="cancellation-date">
                {t("cancellation.confirm.fields.desiredEffectiveOn")}
              </label>
              <input
                id="cancellation-date"
                className={`pub-input${
                  hasError("desiredEffectiveOn") ? " pub-input-invalid" : ""
                }`}
                type="date"
                value={values.desiredEffectiveOn}
                onChange={(event) =>
                  setValue("desiredEffectiveOn", event.target.value)
                }
              />
              <span className="pub-hint">
                {t("cancellation.confirm.hints.desiredEffectiveOn")}
              </span>
              {hasError("desiredEffectiveOn") ? (
                <span className="pub-field-error">
                  {t("cancellation.confirm.errors.desiredEffectiveOn")}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="pub-field">
            <label className="pub-label" htmlFor="cancellation-note">
              {t("cancellation.confirm.fields.note")}{" "}
              <span className="pub-optional">
                {t("cancellation.confirm.optional")}
              </span>
            </label>
            <textarea
              id="cancellation-note"
              className={`pub-textarea${
                hasError("note") ? " pub-input-invalid" : ""
              }`}
              maxLength={MAX_NOTE}
              value={values.note}
              onChange={(event) => setValue("note", event.target.value)}
            />
            {hasError("note") ? (
              <span className="pub-field-error">
                {t("cancellation.confirm.errors.note")}
              </span>
            ) : null}
          </div>

          <button type="submit" className="pub-primary-btn" disabled={busy}>
            {busy
              ? t("cancellation.confirm.checking")
              : t("cancellation.confirm.continue")}
          </button>
          <p className="pub-submit-hint">
            {t("cancellation.confirm.continueHint")}
          </p>
          {/*
            Art. 13 GDPR: the person filling this in has no account here, so the
            information is owed at the moment he types his name and address.
          */}
          <p className="pub-submit-hint">
            {t("cancellation.confirm.privacyHint")}{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pub-link"
            >
              {t("cancellation.confirm.privacyLink")}
            </a>
          </p>
        </section>
      </form>
    </div>
  );
}

export default CancellationConfirmPage;
